import { useMutation, useQuery } from "@apollo/client";
import { Alert, Box, CircularProgress, Snackbar, Tab, Tabs, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { GetSingleUserQuery, GetSingleUserQueryVariables } from "@/graphql/graphql";
import useFlag from "@/hooks/useFlag";
import { AppPageNoTopPadding } from "@/layouts";
import FeatureFlags from "@/utils/featureFlags";

import { Account, Playlist } from "../../types/api";
import CallPlans from "./components/CallPlans";
import { CallSchedules } from "./components/CallSchedules";
import { PlaylistsComponent } from "./components/Playlists";
import { UsageHistory } from "./components/UsageHistory";
import { UserInfoComponent } from "./components/UserInfo";
import { GET_TREATMENT_HISTORY } from "./components/UserInfo/components/TreatmentHistoryTable/queries";
import { TreatmentHistoryResponse } from "./components/UserInfo/components/TreatmentHistoryTable/types";
import { GET_SINGLE_USER, SEND_ACCESS_NOTIFICATION } from "./queries";
import { Loader } from "./styles";
import {
  AccessNotificationInput,
  TabPanelProps,
  USER_DETAILS_TAB_META,
  USER_DETAILS_TAB_ORDER,
  UserDetailsTab,
  userDetailsTabFromSlug,
  userDetailsTabSlug,
} from "./types";

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...other}
  >
    <Box sx={{ p: 3 }}>
      <div>{children}</div>
    </Box>
  </div>
);

const a11yProps = (tabName: string) => ({
  id: `simple-tab-${tabName}`,
  "aria-controls": `simple-tabpanel-${tabName}`,
});

export const UserDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id, tab } = useParams();
  const showCallPlanTabEnabled = useFlag(FeatureFlags.SHOW_CALL_PLAN_TAB);
  const forcePlaylistsTabEnabled = useFlag(FeatureFlags.FORCE_PLAYLISTS_TAB);
  const userId = parseInt(id ?? "") || 0;
  const pathPrefix = `/patient-details/${userId}`;
  const [tabValue, setTabValue] = useState<UserDetailsTab>(
    userDetailsTabFromSlug(tab) ?? UserDetailsTab.PatientInfo
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showError, setShowError] = useState<boolean>(false);

  const handleTabChange = (_event, newValue: UserDetailsTab) => {
    setTabValue(newValue);
  };

  const [sendAccessNotification] = useMutation<AccessNotificationInput>(SEND_ACCESS_NOTIFICATION, {
    variables: {
      accountId: userId,
      accessedBy: localStorage.getItem("username"),
      source: "admin panel",
    },
  });
  useEffect(() => {
    sendAccessNotification();
  }, [sendAccessNotification]);

  const { data, error, loading, refetch } = useQuery<
    GetSingleUserQuery,
    GetSingleUserQueryVariables
  >(GET_SINGLE_USER, {
    variables: { userId },
    fetchPolicy: "network-only",
  });

  const account = data?.account;

  const showPlaylistsTab =
    !showCallPlanTabEnabled || Boolean(account?.playlists?.length) || forcePlaylistsTabEnabled;

  const {
    data: TreatmentData,
    loading: TreatmentLoading,
    refetch: AllProgramsRefetch,
  } = useQuery<TreatmentHistoryResponse>(GET_TREATMENT_HISTORY, {
    variables: { accountId: account?.id },
    fetchPolicy: "network-only",
    skip: !account?.id,
  });

  useEffect(() => {
    setIsLoading(loading || TreatmentLoading);
  }, [loading, TreatmentLoading]);

  useEffect(() => {
    if (error) {
      setShowError(true);
    }
  }, [error]);

  // When user uses back/forward (or manually edits the URL), keep the tab selection in sync.
  useEffect(() => {
    const fromUrl = userDetailsTabFromSlug(tab);
    if (!showCallPlanTabEnabled && fromUrl === UserDetailsTab.CallPlans) {
      navigate(`${pathPrefix}/${userDetailsTabSlug(UserDetailsTab.PatientInfo)}`, {
        replace: true,
      });
      return;
    }

    if (!showPlaylistsTab && fromUrl === UserDetailsTab.Playlists) {
      navigate(`${pathPrefix}/${userDetailsTabSlug(UserDetailsTab.PatientInfo)}`, {
        replace: true,
      });
      return;
    }

    if (fromUrl == null) {
      navigate(`${pathPrefix}/${userDetailsTabSlug(tabValue)}`, { replace: true });
    } else if (fromUrl !== tabValue) {
      setTabValue(fromUrl);
    }
  }, [tab, tabValue, pathPrefix, navigate, showCallPlanTabEnabled, showPlaylistsTab]);

  useEffect(() => {
    if (TreatmentData?.treatmentHistory && TreatmentData.treatmentHistory.length > 0) {
      refetch();
    }
  }, [TreatmentData?.treatmentHistory, refetch]);

  const inMaintenance = account?.latest_program_type?.toLowerCase() === "maintenance";
  const isPatientInfoTabActive = tabValue === UserDetailsTab.PatientInfo;
  const isCallPlansTabActive = showCallPlanTabEnabled && tabValue === UserDetailsTab.CallPlans;
  const isPlaylistsTabActive = showPlaylistsTab && tabValue === UserDetailsTab.Playlists;
  const isUsageHistoryTabActive = tabValue === UserDetailsTab.UsageHistory;
  const isCallSchedulesTabActive = tabValue === UserDetailsTab.CallSchedules;

  return (
    <AppPageNoTopPadding>
      {isLoading || !account ? (
        <Loader>
          <CircularProgress />
        </Loader>
      ) : (
        <Box sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Box
              sx={{
                margin: "1rem 1rem 0 1rem",
                width: "max-content",
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="h6">
                {account?.firstName} {account?.lastName}
              </Typography>
            </Box>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="basic tabs example">
              {USER_DETAILS_TAB_ORDER.map((userDetailsTab) => {
                if (!showCallPlanTabEnabled && userDetailsTab === UserDetailsTab.CallPlans) {
                  return null;
                }

                if (!showPlaylistsTab && userDetailsTab === UserDetailsTab.Playlists) {
                  return null;
                }

                const { slug, label } = USER_DETAILS_TAB_META[userDetailsTab];

                return (
                  <Tab
                    key={slug}
                    component={Link}
                    to={`${pathPrefix}/${slug}`}
                    value={userDetailsTab}
                    label={label}
                    {...a11yProps(slug)}
                  />
                );
              })}
            </Tabs>
          </Box>
          <TabPanel value={tabValue} index={UserDetailsTab.PatientInfo}>
            {isPatientInfoTabActive && (
              <UserInfoComponent
                account={account as Account}
                AllProgramsData={TreatmentData}
                ProgramsReftech={AllProgramsRefetch}
              />
            )}
          </TabPanel>
          {showCallPlanTabEnabled && (
            <TabPanel value={tabValue} index={UserDetailsTab.CallPlans}>
              {isCallPlansTabActive && (
                <CallPlans
                  accountId={userId}
                  callDeliverySpeed={account.callDeliverySpeed}
                  accountTimeZone={account?.timeZone}
                  callPlans={account?.callPlans ?? []}
                  playlists={account?.playlists ?? []}
                />
              )}
            </TabPanel>
          )}
          {showPlaylistsTab && (
            <TabPanel value={tabValue} index={UserDetailsTab.Playlists}>
              {isPlaylistsTabActive && (
                <PlaylistsComponent
                  account={account as Account}
                  AllProgramsData={TreatmentData}
                  ProgramsReftech={AllProgramsRefetch}
                  inMaintenance={inMaintenance}
                />
              )}
            </TabPanel>
          )}
          <TabPanel value={tabValue} index={UserDetailsTab.UsageHistory}>
            {isUsageHistoryTabActive && (
              <UsageHistory account={account as Account} accountId={userId} />
            )}
          </TabPanel>
          <TabPanel value={tabValue} index={UserDetailsTab.CallSchedules}>
            {isCallSchedulesTabActive && (
              <CallSchedules
                callSchedules={account?.callSchedules ?? []}
                userId={userId}
                timeZone={account?.timeZone}
                playlists={(account?.playlists ?? []) as unknown as Playlist[]}
                callPlans={account?.callPlans ?? []}
                inMaintenance={inMaintenance}
              />
            )}
          </TabPanel>
        </Box>
      )}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={showError}
        onClose={() => setShowError(false)}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          Failed to fetch single patient data.
        </Alert>
      </Snackbar>
    </AppPageNoTopPadding>
  );
};
